/**
 * Conflict-check support: name matching between the people named in an intake and everyone the
 * firm already knows (contacts, matter parties, earlier intakes).
 *
 * LawBee NEVER decides whether a conflict exists. It lists possible name matches with the reason
 * for each, ranked, for a lawyer to review and record a decision. Matching is deliberately
 * generous (nicknames, initials, word order, small typos): a false positive costs a lawyer a
 * glance, a false negative could cost much more.
 */

export type MatchReason = 'exact' | 'reordered' | 'nickname' | 'initial' | 'similar' | 'surname';

export type NameMatch = { score: number; reason: MatchReason };

const NICKNAMES: string[][] = [
  ['robert', 'rob', 'bob', 'bobby', 'robbie'],
  ['william', 'will', 'bill', 'billy', 'liam'],
  ['elizabeth', 'liz', 'beth', 'betty', 'eliza', 'lisa'],
  ['james', 'jim', 'jimmy', 'jamie'],
  ['michael', 'mike', 'mick', 'mikey'],
  ['katherine', 'catherine', 'kate', 'katie', 'kathy', 'cathy', 'kat'],
  ['christopher', 'chris', 'kit'],
  ['christine', 'chris', 'christina', 'tina'],
  ['jonathan', 'jon', 'john', 'johnny', 'jack'],
  ['richard', 'rick', 'dick', 'rich', 'richie'],
  ['margaret', 'maggie', 'meg', 'peggy', 'marge'],
  ['thomas', 'tom', 'tommy'],
  ['joseph', 'joe', 'joey'],
  ['daniel', 'dan', 'danny'],
  ['anthony', 'tony'],
  ['alexander', 'alex', 'sandy', 'xander'],
  ['alexandra', 'alex', 'sasha', 'sandra'],
  ['patricia', 'pat', 'patty', 'trish'],
  ['patrick', 'pat', 'paddy'],
  ['jennifer', 'jen', 'jenny'],
  ['matthew', 'matt'],
  ['nicholas', 'nick', 'nico'],
  ['stephen', 'steven', 'steve'],
  ['edward', 'ed', 'eddie', 'ted'],
  ['samuel', 'sam', 'sammy'],
  ['samantha', 'sam', 'sammy'],
  ['benjamin', 'ben', 'benny'],
  ['victoria', 'vicky', 'tori'],
  ['deborah', 'deb', 'debbie'],
  ['susan', 'sue', 'suzy'],
  ['rebecca', 'becky', 'becca'],
  ['andrew', 'andy', 'drew'],
  ['david', 'dave'],
  ['gregory', 'greg'],
  ['timothy', 'tim'],
  ['zachary', 'zach', 'zack'],
];

const NICK_GROUPS = new Map<string, Set<number>>();
NICKNAMES.forEach((group, i) => {
  for (const n of group) {
    const set = NICK_GROUPS.get(n) ?? new Set<number>();
    set.add(i);
    NICK_GROUPS.set(n, set);
  }
});

/** Company suffixes and honorifics that do not distinguish parties. */
const NOISE = new Set(['mr', 'mrs', 'ms', 'miss', 'dr', 'jr', 'sr', 'ii', 'iii', 'inc', 'incorporated', 'ltd', 'limited', 'llc', 'llp', 'corp', 'corporation', 'co', 'company', 'the', 'and', 'of']);

/** Lowercase, strip accents and punctuation, drop honorifics/suffixes, handle "Last, First". */
export function nameTokens(name: string): string[] {
  let s = name.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const comma = s.indexOf(',');
  if (comma > 0) s = `${s.slice(comma + 1)} ${s.slice(0, comma)}`;
  return s
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s'-]/g, ' ')
    .replace(/['-]/g, '')
    .split(/\s+/)
    .filter((t) => t && !NOISE.has(t));
}

export function normalizeName(name: string): string {
  return nameTokens(name).join(' ');
}

/** Levenshtein distance with an early exit above `max`. */
export function editDistance(a: string, b: string, max = 3): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      const v = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      cur.push(v);
      best = Math.min(best, v);
    }
    if (best > max) return max + 1;
    prev = cur;
  }
  return prev[b.length]!;
}

function nicknameMatch(a: string, b: string): boolean {
  const ga = NICK_GROUPS.get(a);
  const gb = NICK_GROUPS.get(b);
  if (!ga || !gb) return false;
  for (const g of ga) if (gb.has(g)) return true;
  return false;
}

function similar(a: string, b: string): boolean {
  if (a === b) return true;
  const len = Math.min(a.length, b.length);
  if (len < 4) return false;
  return editDistance(a, b, 2) <= (len >= 7 ? 2 : 1);
}

type TokenRelation = 'same' | 'nickname' | 'initial' | 'similar' | null;

function relate(a: string, b: string): TokenRelation {
  if (a === b) return 'same';
  if ((a.length === 1 && b.startsWith(a)) || (b.length === 1 && a.startsWith(b))) return 'initial';
  if (nicknameMatch(a, b)) return 'nickname';
  if (similar(a, b)) return 'similar';
  return null;
}

/**
 * Compare two names. Returns null when they are clearly different people/entities. Scores:
 * exact 100, reordered 95, nickname 85, initial 75, similar spelling 70, surname-only 40.
 */
export function compareNames(a: string, b: string): NameMatch | null {
  const ta = nameTokens(a);
  const tb = nameTokens(b);
  if (ta.length === 0 || tb.length === 0) return null;
  if (ta.join(' ') === tb.join(' ')) return { score: 100, reason: 'exact' };
  if ([...ta].sort().join(' ') === [...tb].sort().join(' ')) return { score: 95, reason: 'reordered' };

  // Single-token names (companies often reduce to one token after suffix removal).
  if (ta.length === 1 || tb.length === 1) {
    const [one, many] = ta.length === 1 ? [ta, tb] : [tb, ta];
    if (many.length === 1 && similar(one[0]!, many[0]!)) return { score: 70, reason: 'similar' };
    return null;
  }

  // Multi-token people: last token is the surname, the rest given names.
  const surnameA = ta[ta.length - 1]!;
  const surnameB = tb[tb.length - 1]!;
  const surnameRel = relate(surnameA, surnameB);
  if (surnameRel !== 'same' && surnameRel !== 'similar') {
    // Try reversed order ("Smith John" vs "John Smith") without a comma.
    const rev = relate(ta[0]!, surnameB) === 'same' && relate(surnameA, tb[0]!) === 'same';
    return rev ? { score: 95, reason: 'reordered' } : null;
  }
  const givenA = ta[0]!;
  const givenB = tb[0]!;
  const givenRel = relate(givenA, givenB);
  const surnameSimilar = surnameRel === 'similar';
  switch (givenRel) {
    case 'same':
      return surnameSimilar ? { score: 70, reason: 'similar' } : { score: 100 - (ta.length !== tb.length ? 5 : 0), reason: ta.length !== tb.length ? 'reordered' : 'exact' };
    case 'nickname':
      return { score: surnameSimilar ? 65 : 85, reason: 'nickname' };
    case 'initial':
      return { score: surnameSimilar ? 60 : 75, reason: 'initial' };
    case 'similar':
      return { score: surnameSimilar ? 55 : 70, reason: 'similar' };
    default:
      return surnameSimilar ? null : { score: 40, reason: 'surname' };
  }
}

export type ConflictCandidate = {
  /** contact | matter_party | intake | intake_party */
  source: 'contact' | 'matter_party' | 'intake' | 'intake_party';
  id: string;
  name: string;
  role?: string | null;
  matterId?: string | null;
  matterLabel?: string | null;
  intakeId?: string | null;
};

export type ConflictHit = ConflictCandidate & { searched: string; searchedRole: string; score: number; reason: MatchReason };

export const REASON_LABELS: Record<MatchReason, string> = {
  exact: 'Exact name match',
  reordered: 'Same name, different order or middle name',
  nickname: 'Possible nickname of the same first name',
  initial: 'Initial matches the first name',
  similar: 'Similar spelling',
  surname: 'Same surname (different first name)',
};

/**
 * Search every searched name (the prospective client and each party) against the candidates.
 * Surname-only matches are included only when `includeSurnameOnly` (default true; they are cheap
 * to dismiss and can reveal family relationships). Sorted by score, then name.
 */
export function searchConflicts(
  searched: { name: string; role: string }[],
  candidates: ConflictCandidate[],
  options: { minScore?: number; includeSurnameOnly?: boolean; excludeIntakeId?: string } = {},
): ConflictHit[] {
  const minScore = options.minScore ?? 40;
  const hits: ConflictHit[] = [];
  const seen = new Set<string>();
  for (const s of searched) {
    if (!s.name.trim()) continue;
    for (const c of candidates) {
      if (options.excludeIntakeId && c.intakeId === options.excludeIntakeId) continue;
      const m = compareNames(s.name, c.name);
      if (!m || m.score < minScore) continue;
      if (m.reason === 'surname' && options.includeSurnameOnly === false) continue;
      const key = `${s.name}|${c.source}|${c.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      hits.push({ ...c, searched: s.name, searchedRole: s.role, score: m.score, reason: m.reason });
    }
  }
  return hits.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
}

export const CONFLICT_DISCLAIMER =
  'LawBee lists possible name matches only. It does not decide whether a conflict of interest exists; a lawyer must review the results and record a decision.';
